import React, { useEffect, useState } from 'react';
import { append, includes, length, map, propEq } from 'ramda';
import PropTypes from 'prop-types';
import { DashedTitle, Icon, Loading } from '@fasstech/middle-design-system';
import SendMailModal from '../SendMailModal';
import useDownloadDocumentsContainer from './useDownloadDocumentsContainer';
import DownloadButton from './DownloadButton';
import { lengthEq } from '@@serverHelpers/index.mjs';
import ExtraDownloadContainer from './ExtraDownloadContainer';
import displayExtraEmailButton
  from '@@viewCommonComponents/displayer/DownloadDocumentsContainer/extra/displayExtraEmailButton.js';
import ErrorOnSaveDocumentsModal from '../ErrorOnSaveDocumentsModal';
import { isNotNilOrEmpty } from '@@serverHelpers';

const DownloadDocumentsContainer = ({
  downloadMutation,
  documentsList,
  userEmail,
  customerEmail,
  customerCivility,
  customerLastName,
  customerFirstName,
  userFirstName,
  userLastName,
  sendEmailMutation,
  setDocumentsDownloaded = () => {
  },
  getMiddleText,
  downloadMandatory = true,
  setCanSendMail,
  canSendMail
}) => {
  const {
    methods,
    setDisplayModal,
    handleLoadDocuments,
    loader,
    displayModal,
    hasError,
    errorSaveDocumentModalMessage,
    setErrorSaveDocumentModalMessage
  } = useDownloadDocumentsContainer({
    downloadMutation,
    setDocumentsDownloaded,
    getMiddleText
  });

  // track which documents have been downloaded
  const [downloadedDocuments, setDownloadedDocuments] = useState([]);

  // Update downloadedDocuments
  const handleDocumentDownload = (document) => {
    handleLoadDocuments([document], document.label);
    if (!includes(document.bundleTag, downloadedDocuments)) {
      setDownloadedDocuments(append(document.bundleTag, downloadedDocuments));
    }
  };

  // check if all documents have been downloaded
  useEffect(() => {
    if (lengthEq(length(documentsList), downloadedDocuments) && !loader.isLoading && !hasError) {
      setDocumentsDownloaded(true);
    }
  }, [downloadedDocuments, documentsList.length, setDocumentsDownloaded, hasError, loader]);

  return (
    <div className={'flex flex-col gap-4 w-full md:w-[90%]!'}>
      <DashedTitle className={'flex justify-center items-center'}>
        {getMiddleText('RECAPITULATIF_DOCUMENTS_TITLE')}
      </DashedTitle>

      <div className="p-4 bg-[#F9F9FF] rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2! gap-4">
          {map(({
            option,
            label,
            bundleTag,
            featureName,
            productCode,
            filePath
          }) => (
            <div
              className="flex items-center justify-between bg-white px-4 py-2 rounded-lg cursor-pointer w-full"
              key={`${option}-${label}`}>
              <div className="flex items-center gap-2 truncate">
                <Icon name="sheet" size={18} strokeClassName="stroke-black"/>
                <div className="font-light truncate">{label}</div>
              </div>
              <div className="flex items-center ml-auto min-w-[28px]">
                {loader.isLoading && propEq('name', label, loader) && <Loading size="small"/>}
                <Icon
                  name="circleDownload"
                  size={28}
                  className="ml-2"
                  onClick={() =>
                    handleDocumentDownload({
                      bundleTag,
                      label,
                      featureName,
                      productCode,
                      filePath
                    })
                  }
                  data-testid={`iconDownload${bundleTag}`}
                />
              </div>
            </div>
          ))(documentsList)}
        </div>

        <ExtraDownloadContainer hasError={hasError} downloadMandatory={downloadMandatory}/>

        <div className="flex flex-col md:flex-row! items-end md:justify-end! mt-4 gap-4">
          <DownloadButton
            icon="send"
            onClick={() => setDisplayModal(true)}
            text={getMiddleText('SEND_DOCUMENTS_EMAIL_BUTTON')}
            isDisplayed={displayExtraEmailButton()}
          />

          <DownloadButton
            icon="download"
            onClick={() => {
              handleLoadDocuments(documentsList, 'zip');
              setDownloadedDocuments(map((document) => document.bundleTag, documentsList));
            }}
            text={getMiddleText('DOWNLOAD_DOCUMENTS_BUTTON')}
            isLoading={loader.isLoading && propEq('name', 'zip', loader)}
            disabled={loader.isLoading && propEq('name', 'zip', loader)}
          />
        </div>
      </div>

      <SendMailModal
        isDisplayed={displayModal}
        isLoading={loader.isLoading}
        sendEmailMutation={sendEmailMutation}
        watch={methods.watch}
        setDisplayModal={setDisplayModal}
        userEmail={userEmail}
        customerEmail={customerEmail}
        customerCivility={customerCivility}
        customerLastName={customerLastName}
        customerFirstName={customerFirstName}
        userFirstName={userFirstName}
        userLastName={userLastName}
        setDocumentsDownloaded={setDocumentsDownloaded}
        filesToSend={documentsList}
        getMiddleText={getMiddleText}
        setErrorSaveDocumentModalMessage={setErrorSaveDocumentModalMessage}
        canSendMail={canSendMail}
        setCanSendMail={setCanSendMail}
      />

      <ErrorOnSaveDocumentsModal
        isDisplayed={isNotNilOrEmpty(errorSaveDocumentModalMessage)}
        errorSaveDocumentModalMessage={errorSaveDocumentModalMessage}
        setErrorSaveDocumentModalMessage={setErrorSaveDocumentModalMessage}
        onClick2={() => setErrorSaveDocumentModalMessage('')}
        button2Label={'OK'}
        button1Label={''}
        button2Variant={'primary'}
      />
    </div>
  );
};

DownloadDocumentsContainer.propTypes = {
  downloadMutation: PropTypes.func,
  sendEmailMutation: PropTypes.func,
  documentsList: PropTypes.array,
  userEmail: PropTypes.string,
  customerEmail: PropTypes.string,
  isSyntheseStep: PropTypes.bool,
  contributorName: PropTypes.string
};

export default DownloadDocumentsContainer;
